import type { ZoneLayout } from '../../zones/types';

/**
 * Waystone network (≈ triports). Each waystone interactable maps to its zone
 * and the zone's spawn point nearest to it, which is where travel lands.
 */

export interface WaystoneInfo {
  id: string;
  name: string;
  zone: string;
  zoneName: string;
  zoneKind: string;
  levelRange: [number, number];
  spawn: string;
  x: number;
  z: number;
}

/** Overworld zones that can hold waystones, in map order. */
export const OVERWORLD_IDS = [
  'havenreach',
  'emberwild',
  'sunscar',
  'frosthollow',
  'haven',
  'glimmer',
];

/** Zone kinds a player may travel from (not from inside instances). */
export const TRAVEL_FROM = new Set(['field', 'town', 'haven', 'island']);

export function nearestSpawn(layout: ZoneLayout, x: number, z: number): string {
  let best = 'start';
  let bestD = Infinity;
  for (const [name, sp] of Object.entries(layout.spawnPoints)) {
    const d = Math.hypot(sp.x - x, sp.z - z);
    if (d < bestD) {
      bestD = d;
      best = name;
    }
  }
  return best;
}

export function waystonesIn(layout: ZoneLayout): WaystoneInfo[] {
  return layout.interactables
    .filter((it) => it.kind === 'waystone')
    .map((it) => ({
      id: it.id,
      name: String(it.data?.name ?? layout.name),
      zone: layout.id,
      zoneName: layout.name,
      zoneKind: layout.kind,
      levelRange: [layout.levelRange[0], layout.levelRange[1]],
      spawn: nearestSpawn(layout, it.x, it.z),
      x: it.x,
      z: it.z,
    }));
}

/** Silver for a jump: scales with character level, half price within a zone. */
export function travelCost(level: number, sameZone: boolean): number {
  const base = 20 + 6 * Math.max(1, level);
  return Math.max(10, Math.round(sameZone ? base / 2 : base));
}
